import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CustomModal from '../custom_modal';
import CustomRadioButton from '../custom_radio_button';
import { FormActions, FormHelp } from '../form_layout';
import RequiredReasonTextarea from '../required_reason_textarea';
import { care_api_error, format_ist } from './care_request_rules';
import { resolve_care_request } from './care_request_api';
import {
  answer_label,
  build_resolve_payload,
  new_resolve_draft,
  resolve_action_label,
  validate_resolve_draft,
  who_asked_view
} from './who_asked_rules';

const ANSWERS = ['STUDENT', 'TEACHER'];

const move_label = (c) =>
  `${format_ist(c.at)}${(c.changes || [])
    .map((ch) => ` · ${ch.label}: ${ch.from || '--'} → ${ch.to || '--'}`)
    .join('')}`;

// resolve step: Care or the payments team sets the final "who asked" and/or the move a Care note
// belongs to, with a required reason. Teacher pay follows the final "who asked" (the month
// recalculates).
function ResolveDialog({ record_uuid, who_asked, match, parts, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => new_resolve_draft(who_asked));
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, error: '' });

  useEffect(() => {
    setDraft(new_resolve_draft(who_asked));
    setErrors({});
    setState({ busy: false, error: '' });
  }, [record_uuid, who_asked]);

  const save = async () => {
    const found = validate_resolve_draft(draft, parts);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setState({ busy: true, error: '' });
    try {
      await resolve_care_request(record_uuid, build_resolve_payload(draft, parts));
      setState({ busy: false, error: '' });
      onSaved();
    } catch (e) {
      setState({ busy: false, error: care_api_error(e, 'Could not save. Try again.').message });
    }
  };

  const view = who_asked_view(who_asked);
  const title = resolve_action_label(who_asked, match);
  return (
    <CustomModal show={!!record_uuid} close={() => (state.busy ? null : onClose())} variant="form">
      {record_uuid ? (
        <div role="dialog" aria-label={`${title} change request`} className="resolve-dialog">
          <h5>{title}</h5>
          {parts.answer ? (
            <div className="resolve-dialog-group">
              <div id="resolve-answer-heading" className="myt-form-label">
                Who asked for the change? (final answer)
              </div>
              {view.detail ? <FormHelp>{view.detail}</FormHelp> : null}
              <div
                role="radiogroup"
                aria-labelledby="resolve-answer-heading"
                className="myt-form-radios"
              >
                {ANSWERS.map((v) => (
                  <CustomRadioButton
                    key={v}
                    label={answer_label(v)}
                    selected={draft.resolved_value === v}
                    onRadioClick={() => setDraft({ ...draft, resolved_value: v })}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {parts.match ? (
            <div className="resolve-dialog-group">
              <div id="resolve-move-heading" className="myt-form-label">
                Which move does this note belong to?
              </div>
              <div role="radiogroup" aria-labelledby="resolve-move-heading">
                {((match && match.candidates) || []).map((c) => (
                  <div key={c.audit_id} className="resolve-dialog-option">
                    <CustomRadioButton
                      label={move_label(c)}
                      selected={String(draft.matched_audit_id) === String(c.audit_id)}
                      onRadioClick={() => setDraft({ ...draft, matched_audit_id: c.audit_id })}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {errors.choice ? <p className="myt-form-error">* {errors.choice}</p> : null}
          <RequiredReasonTextarea
            label="Reason"
            value={draft.reason}
            onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            placeholder="Why is this the right answer? (required)"
            error={!!errors.reason}
            errorText={errors.reason}
            height="60px"
          />
          {state.error ? <p className="myt-form-error">* {state.error}</p> : null}
          <FormActions>
            <button type="button" disabled={state.busy} onClick={save}>
              {state.busy ? 'Saving…' : 'Save'}
            </button>
          </FormActions>
        </div>
      ) : null}
    </CustomModal>
  );
}

ResolveDialog.propTypes = {
  record_uuid: PropTypes.string,
  who_asked: PropTypes.shape({ state: PropTypes.string, resolution: PropTypes.shape({}) }),
  match: PropTypes.shape({
    state: PropTypes.string,
    candidates: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  parts: PropTypes.shape({ answer: PropTypes.bool, match: PropTypes.bool }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

ResolveDialog.defaultProps = {
  record_uuid: '',
  who_asked: null,
  match: null
};

export default ResolveDialog;
